import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { AppBarCMS } from '../../../../../basic/index.jsx';
import { CollapseBlock, Spinner } from '../../../../../common/index.jsx';
import { routes } from '../../../../../../utils/helpers/ApiClient.jsx';
import { useToastMessages } from '../../../../../../hooks/useToastMessages.jsx';
import {
  useDeleteQuizMutation,
  useGetQuizzesQuery,
} from '../../../../../../redux/services/quizzes.jsx';
import QuizzesTable from './QuizzesTable.jsx';

const QuizzesPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // State for expanded quizzes
  const [expandedRowKeys, setExpandedRowKeys] = useState(() => {
    // Load from localStorage
    try {
      const saved = localStorage.getItem('quiz-table-expanded-rows');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    document.title = t('Kvízy') + ' - FRI CMS';
  }, [t]);

  const { deleteSuccess: showDeleteSuccess } = useToastMessages();
  const { data: quizzes, isLoading, refetch } = useGetQuizzesQuery();
  const [deleteQuiz, { isSuccess: isDeleteSuccess }] = useDeleteQuizMutation();

  // Show toast only on transition false -> true; rely on tag invalidation for refetch
  const prevDeleteSuccessRef = useRef(false);
  useEffect(() => {
    if (isDeleteSuccess && !prevDeleteSuccessRef.current) {
      showDeleteSuccess();
    }
    prevDeleteSuccessRef.current = isDeleteSuccess;
  }, [isDeleteSuccess, showDeleteSuccess]);

  const expandQuizId = location.state?.expandQuizId;

  // Handle programmatic expansion from navigation state
  React.useEffect(() => {
    if (expandQuizId && !expandedRowKeys.includes(expandQuizId)) {
      const newExpanded = [...expandedRowKeys, expandQuizId];
      setExpandedRowKeys(newExpanded);
      localStorage.setItem(
        'quiz-table-expanded-rows',
        JSON.stringify(newExpanded),
      );
    }
    if (expandQuizId) {
      // replace state with empty object
      navigate(location.pathname, { replace: true });
    }
  }, [expandQuizId, expandedRowKeys, navigate, location.pathname]);

  // Save expanded rows to localStorage whenever they change
  const handleExpandedRowsChange = (newExpandedRowKeys) => {
    setExpandedRowKeys(newExpandedRowKeys);
    localStorage.setItem(
      'quiz-table-expanded-rows',
      JSON.stringify(newExpandedRowKeys),
    );
  };

  return isLoading ? (
    <Spinner />
  ) : (
    <>
      <AppBarCMS
        title={t('Kvízy')}
        backUrl={routes.cmsDashboard}
        backUrlTooltip={t('Nástenka')}
        permissionsBasic="quizzes"
        createText={t('Pridať kvíz')}
        createUrl={routes.cmsQuizzesCreate}
      />
      <div style={{ margin: '10px' }}>
        <CollapseBlock>
          <QuizzesTable
            quizzes={quizzes}
            onDelete={(record) => deleteQuiz(record.id)}
            onDetailClick={(record) =>
              navigate(`${routes.cmsQuizzesEdit}/${record.id}`)
            }
            onAddQuestion={(record) =>
              navigate(`${routes.cmsQuizQuestionsCreate}?quizId=${record.id}`)
            }
            expandedRowKeys={expandedRowKeys}
            onExpandedRowsChange={handleExpandedRowsChange}
          />
        </CollapseBlock>
      </div>
    </>
  );
};

export default QuizzesPage;
